import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import axios from 'axios';
import { Calendar as CalendarIcon, Clock, ArrowLeft, CheckCircle, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { companyInfo, services } from '../mock';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import BackButton from '../components/BackButton';
import { trackRDVSubmit } from '../components/GoogleAnalytics';
import { trackGenerateLead } from '../utils/analytics';
import { executeReCaptcha } from '../utils/recaptcha';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../components/LanguageSwitcher';
import VilleAutocomplete from '../components/VilleAutocomplete';
import AntiSpamFields from '../components/AntiSpamFields';

const RDV = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const currentLocale = i18n.language === 'fr' ? fr : enUS;

  useEffect(() => {
    document.title = 'Prendre Rendez-vous - Consultation Gratuite | DumaExpertBéton';
    const descMeta = document.querySelector('meta[name="description"]');
    if (descMeta) descMeta.setAttribute('content', 'Prenez rendez-vous pour une consultation gratuite béton décoratif. Visite sur site, devis personnalisé. Expert PACA depuis 2014.');
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = 'https://duma-expert-beton.fr/rdv';
    return () => { document.title = 'DumaExpertBéton - Expert en Béton Imprimé et Décoratif | Orange, Vaucluse'; };
  }, []);
  
  const handleBack = () => {
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/');
    }
  };
  const [date, setDate] = useState();
  const [selectedTime, setSelectedTime] = useState('');
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    ville: '',
    service: '',
    notes: '',
    gdpr_consent: false,
    honeypot: '' // Honeypot field
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const antiSpamRef = React.useRef(null);

  // Reset selected time when date changes and no slots are available
  useEffect(() => {
    if (date && selectedTime) {
      // Available time slots
      const timeSlots = [
        '08:00', '09:00', '10:00', '11:00',
        '14:00', '15:00', '16:00', '17:00'
      ];
      
      const now = new Date();
      const selectedDate = new Date(date);
      
      // Normalize dates to compare only day/month/year
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const selected = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
      
      // Check if selected date is today
      const isToday = selected.getTime() === today.getTime();
      
      let availableSlots = timeSlots;
      
      if (isToday) {
        // Today - filter past hours with 2 hour buffer for preparation
        const currentHour = now.getHours();
        const minAvailableHour = currentHour + 2;
        
        availableSlots = timeSlots.filter(slot => {
          const slotHour = parseInt(slot.split(':')[0]);
          return slotHour >= minAvailableHour;
        });
      }
      
      if (availableSlots.length === 0 || !availableSlots.includes(selectedTime)) {
        setSelectedTime('');
      }
    }
  }, [date, selectedTime]);

  // Available time slots
  const timeSlots = [
    '08:00', '09:00', '10:00', '11:00',
    '14:00', '15:00', '16:00', '17:00'
  ];

  // Filter time slots based on selected date
  const getAvailableTimeSlots = () => {
    if (!date) return timeSlots;
    
    const now = new Date();
    const selectedDate = new Date(date);
    
    // Normalize dates to compare only day/month/year
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const selected = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
    
    // Check if selected date is today
    const isToday = selected.getTime() === today.getTime();
    
    if (!isToday) {
      // Future date - all slots available
      return timeSlots;
    }
    
    // Today - filter past hours with 2 hour buffer for preparation
    const currentHour = now.getHours();
    const minAvailableHour = currentHour + 2;
    
    const available = timeSlots.filter(slot => {
      const slotHour = parseInt(slot.split(':')[0]);
      return slotHour >= minAvailableHour;
    });
    
    console.log('Current hour:', currentHour, 'Min available:', minAvailableHour, 'Available slots:', available);
    return available;
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handleServiceChange = (value) => {
    setFormData({
      ...formData,
      service: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Honeypot check
    if (formData.honeypot) {
      console.log('Bot detected - honeypot filled');
      return;
    }
    
    if (!date || !selectedTime) {
      alert(t('rdv.pleaseSelectDateTime'));
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Get reCAPTCHA token
      const recaptchaToken = await executeReCaptcha('rdv_booking');
      
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const appointmentData = {
        ...formData,
        date: format(date, 'dd/MM/yyyy'),
        time: selectedTime,
        recaptchaToken,
        cf_turnstile_token: turnstileToken,
        website: antiSpamRef.current?.getHoneypotValue() || ''
      };
      
      const response = await axios.post(`${BACKEND_URL}/api/appointments`, appointmentData);
      
      if (response.data.success) {
        // Track conversion in Google Analytics
        trackRDVSubmit(appointmentData);
        trackGenerateLead('rdv_submit');
        setIsSubmitted(true);
      }
    } catch (error) {
      console.error('Error booking appointment:', error);
      alert(t('rdv.errorOccurred'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const day = date.getDay();
    // Disable past dates and Sundays (0)
    return date < today || day === 0;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 transition-colors">
      <SEO 
        title={`${t('rdv.title')} - DumaExpertBéton`}
        description={t('rdv.subtitle')}
        url="https://dumaexpertbeton.fr/rdv"
      />
      
      {/* Language Switcher */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>
      
      {/* Header */}
      <div className="bg-white dark:bg-slate-900 py-8 px-4 sm:px-6 lg:px-8 transition-colors">
        <div className="container mx-auto">
          <button onClick={handleBack} className="inline-flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-800/50 hover:bg-slate-200 dark:hover:bg-slate-700/50 rounded-lg border border-slate-300 dark:border-slate-700 transition-all duration-200 mb-4 cursor-pointer">
            <ArrowLeft className="w-4 h-4" />
            <span className="font-medium">{t('common.back')}</span>
          </button>
          <h1 className="text-4xl sm:text-5xl font-bold mt-4 text-slate-900 dark:text-white transition-colors">{t('rdv.title')}</h1>
          <p className="text-xl text-slate-700 dark:text-slate-300 mt-4 transition-colors">{t('rdv.subtitle')}</p>
        </div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {isSubmitted ? (
          <Card className="max-w-2xl mx-auto bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700">
            <CardContent className="text-center py-12">
              <CheckCircle className="w-20 h-20 text-green-600 dark:text-green-400 mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-4">{t('rdv.success')}</h2>
              <p className="text-xl text-slate-600 dark:text-slate-300 mb-2">
                {date && format(date, "EEEE d MMMM yyyy", { locale: currentLocale })}
              </p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white mb-6">{t('rdv.at')} {selectedTime}</p>
              <p className="text-slate-600 dark:text-slate-300 mb-8">
                {t('rdv.thankYou')} <strong className="text-slate-900 dark:text-white">{formData.email}</strong>
              </p>
              <div className="bg-slate-50 dark:bg-slate-700 p-6 rounded-lg mb-8">
                <p className="text-slate-700 dark:text-slate-200 mb-4">
                  {t('rdv.contactBefore')}
                </p>
                <div className="flex items-center justify-center gap-2 text-slate-900 dark:text-white">
                  <Phone className="w-5 h-5" />
                  <a href={`tel:${companyInfo.phoneRaw}`} className="font-semibold hover:text-[#C97249] dark:hover:text-[#E97C30]">
                    {companyInfo.phone}
                  </a>
                </div>
              </div>
              <Link to="/">
                <Button size="lg" className="bg-slate-900 hover:bg-slate-800">
                  {t('rdv.backHome')}
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {/* Calendar Section */}
            <Card className="bg-white dark:bg-slate-800 border-2 border-slate-300 dark:border-slate-700 shadow-xl transition-colors">
              <CardHeader className="bg-gradient-to-r from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 text-slate-900 dark:text-white rounded-t-lg transition-colors">
                <CardTitle className="text-2xl flex items-center gap-2 transition-colors">
                  <CalendarIcon className="w-6 h-6 text-orange-400" />
                  {t('rdv.selectDate')}
                </CardTitle>
                <CardDescription className="text-slate-700 dark:text-slate-300 transition-colors">
                  {t('rdv.selectDateDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex flex-col items-center py-8">
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={isDateDisabled}
                  locale={fr}
                  className="rounded-lg border-2 border-slate-300 dark:border-slate-700 bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 p-4 shadow-2xl transition-colors"
                  classNames={{
                    months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                    month: "space-y-4",
                    caption: "flex justify-center pt-1 relative items-center mb-4",
                    caption_label: "text-xl font-bold text-slate-900 dark:text-white transition-colors",
                    nav: "space-x-1 flex items-center",
                    nav_button: "h-8 w-8 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-900 dark:text-white rounded-md transition-all hover:scale-110",
                    nav_button_previous: "absolute left-1",
                    nav_button_next: "absolute right-1",
                    table: "w-full border-collapse space-y-1",
                    head_row: "flex justify-around mb-2",
                    head_cell: "text-slate-700 dark:text-slate-300 rounded-md w-10 font-semibold text-sm uppercase transition-colors",
                    row: "flex w-full mt-2 justify-around",
                    cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20",
                    day: "h-10 w-10 p-0 font-semibold rounded-lg hover:bg-orange-500 hover:text-white transition-all hover:scale-110 text-slate-900 dark:text-white bg-slate-100 dark:bg-slate-700",
                    day_selected: "bg-gradient-to-br from-orange-600 to-red-600 text-white font-bold border-2 border-orange-400 shadow-lg shadow-orange-500/50",
                    day_today: "bg-emerald-600 text-white font-bold ring-2 ring-emerald-400 ring-offset-2 ring-offset-slate-100 dark:ring-offset-slate-800",
                    day_outside: "text-slate-400 dark:text-slate-500 opacity-40",
                    day_disabled: "text-slate-400 dark:text-slate-600 opacity-30 line-through hover:bg-slate-100 dark:hover:bg-slate-700 hover:scale-100 cursor-not-allowed",
                    day_range_middle: "aria-selected:bg-slate-300 dark:aria-selected:bg-slate-600 aria-selected:text-slate-900 dark:aria-selected:text-white",
                    day_hidden: "invisible",
                  }}
                />
                
                {date && (
                  <div className="mt-6 w-full max-w-md">
                    <Label className="flex items-center gap-2 mb-3 text-slate-900 dark:text-white text-lg font-bold transition-colors">
                      <Clock className="w-6 h-6 text-orange-600" />
                      {t('rdv.selectTime')}
                    </Label>
                    {getAvailableTimeSlots().length === 0 ? (
                      <div className="p-5 bg-red-100 dark:bg-red-900/30 border-3 border-red-500 dark:border-red-600 rounded-lg text-center transition-colors">
                        <p className="text-red-800 dark:text-red-200 font-black text-lg transition-colors">
                          {t('rdv.noSlotsToday')}
                        </p>
                        <p className="text-red-700 dark:text-red-300 font-bold text-sm mt-2 transition-colors">
                          {t('rdv.tooLateToday')}
                        </p>
                        <p className="text-red-600 dark:text-red-400 text-sm mt-1 transition-colors">
                          {t('rdv.selectFutureDate')}
                        </p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-4 gap-3">
                        {getAvailableTimeSlots().map((time) => (
                          <Button
                            key={time}
                            type="button"
                            variant={selectedTime === time ? "default" : "outline"}
                            className={selectedTime === time 
                              ? "bg-gradient-to-r from-orange-600 to-red-600 text-white font-black hover:from-orange-700 hover:to-red-700 shadow-lg border-2 border-red-700" 
                              : "border-2 border-slate-400 dark:border-slate-600 text-slate-900 dark:text-slate-200 font-bold hover:bg-yellow-100 dark:hover:bg-orange-900/30 hover:text-orange-700 dark:hover:text-orange-400 hover:border-orange-500 hover:scale-110 transition-all"}
                            onClick={() => setSelectedTime(time)}
                          >
                            {time}
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {date && selectedTime && (
                  <div className="mt-6 p-5 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 rounded-lg border-3 border-green-600 dark:border-green-500 shadow-lg transition-colors">
                    <p className="text-sm text-green-700 dark:text-green-400 mb-2 font-bold uppercase tracking-wide transition-colors">{t('rdv.appointmentSelected')}</p>
                    <p className="font-black text-slate-900 dark:text-white text-xl transition-colors">
                      {format(date, "EEEE d MMMM yyyy", { locale: currentLocale })}
                    </p>
                    <p className="font-black text-orange-600 dark:text-orange-400 text-2xl mt-1 transition-colors">
                      🕐 {selectedTime}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Form Section */}
            <Card className="bg-white dark:bg-slate-800 border-2 border-slate-300 dark:border-slate-700 shadow-xl transition-colors">
              <CardHeader className="bg-gradient-to-r from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 text-slate-900 dark:text-white rounded-t-lg transition-colors">
                <CardTitle className="text-2xl transition-colors">{t('rdv.yourInfo')}</CardTitle>
                <CardDescription className="text-slate-700 dark:text-slate-300 transition-colors">
                  {t('rdv.yourInfoDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="nom">{t('rdv.lastName')} *</Label>
                      <Input 
                        id="nom"
                        name="nom"
                        value={formData.nom}
                        onChange={handleChange}
                        required
                        placeholder={t('rdv.lastName')}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="prenom">{t('rdv.firstName')} *</Label>
                      <Input 
                        id="prenom"
                        name="prenom"
                        value={formData.prenom}
                        onChange={handleChange}
                        required
                        placeholder={t('rdv.firstName')}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">{t('rdv.email')} *</Label>
                    <Input 
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="votre@email.fr"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="telephone">{t('rdv.phone')} *</Label>
                    <Input 
                      id="telephone"
                      name="telephone"
                      type="tel"
                      value={formData.telephone}
                      onChange={handleChange}
                      required
                      placeholder="06 XX XX XX XX"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ville">{t('rdv.city')} *</Label>
                    <VilleAutocomplete
                      id="ville"
                      name="ville"
                      value={formData.ville}
                      onChange={handleChange}
                      required
                      placeholder="Orange, Avignon, Carpentras..."
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="service">{t('rdv.desiredService')} *</Label>
                    <Select value={formData.service} onValueChange={handleServiceChange} required>
                      <SelectTrigger>
                        <SelectValue placeholder={t('rdv.selectService')} />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((service) => (
                          <SelectItem key={service.id} value={service.slug}>
                            {service.title}
                          </SelectItem>
                        ))}
                        <SelectItem value="consultation">{t('rdv.generalConsultation')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">{t('rdv.notes')}</Label>
                    <Input 
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleChange}
                      placeholder={t('rdv.notesPlaceholder')}
                    />
                  </div>

                  {/* Honeypot field - hidden from users */}
                  <div style={{ position: 'absolute', left: '-5000px' }} aria-hidden="true">
                    <input
                      type="text"
                      name="honeypot"
                      value={formData.honeypot}
                      onChange={handleChange}
                      tabIndex="-1"
                      autoComplete="off"
                    />
                  </div>

                  {/* GDPR Consent Checkbox */}
                  <div className="flex items-start gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <input
                      type="checkbox"
                      id="gdpr_consent"
                      name="gdpr_consent"
                      checked={formData.gdpr_consent}
                      onChange={handleChange}
                      required
                      className="mt-1 h-4 w-4 text-slate-900 focus:ring-slate-900 border-slate-300 rounded cursor-pointer"
                    />
                    <label htmlFor="gdpr_consent" className="text-sm text-slate-700 cursor-pointer">
                      {t('rdv.gdprConsent')}{' '}
                      <Link to="/confidentialite" target="_blank" className="text-slate-900 font-semibold underline hover:text-slate-700">
                        {t('rdv.privacyPolicy')}
                      </Link>
                      {t('rdv.gdprRequired')}
                    </label>
                  </div>

                  <AntiSpamFields ref={antiSpamRef} onTurnstileVerify={setTurnstileToken} />

                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full bg-slate-900 hover:bg-slate-800"
                    disabled={isSubmitting || !date || !selectedTime || !formData.gdpr_consent}
                  >
                    {isSubmitting ? t('rdv.confirming') : t('rdv.confirm')}
                  </Button>

                  <p className="text-sm text-slate-500 text-center">
                    {t('rdv.confirmMessage')}
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default RDV;